'use client'

import { clsx } from 'clsx'
import {
  type ComponentProps,
  type CSSProperties,
  type ElementType,
  type FC,
  useMemo,
} from 'react'

import {
  type GkAirRecommendedProductDataWeb,
  type GkAirRecommendedProductWeb,
} from '@redteclab/api/clients/crosssellers-proxy-service'

import { useGlobalConfigContext } from '../../../global-config'
import { SliderButton, useSliderNavigation } from '../../../slider'
import { recommendationProductIsRenderable } from '../../model'
import { useRecommendationWidgetContext } from '../RecommendationAreaProvider'
import { RecommendationProductCard } from '../RecommendationProductCard'

type RecommendationHorizontalSliderGtmDataLayerPushHandler = (
  widgetProductId: GkAirRecommendedProductDataWeb['UID'],
  trackingToken: GkAirRecommendedProductWeb['attributes']['trackingtoken'],
) => void

type RecommendationHorizontalSliderProductLinkClickHandler = (
  widgetProductId: GkAirRecommendedProductDataWeb['UID'],
) => void

type RecommendationHorizontalSliderProps = Omit<
  ComponentProps<'div'>,
  'children'
> & {
  onGtmDataLayerPush?: RecommendationHorizontalSliderGtmDataLayerPushHandler
  onNavigationButtonClick?: () => void
  onProductLinkClick?: RecommendationHorizontalSliderProductLinkClickHandler
  products?: GkAirRecommendedProductWeb[]
  productsPerPage: number
  slots?: {
    itemContent?: ElementType
  }
}

export const RecommendationHorizontalSlider: FC<
  RecommendationHorizontalSliderProps
> = ({
  className,
  onGtmDataLayerPush,
  onNavigationButtonClick,
  onProductLinkClick,
  products,
  productsPerPage,
  slots,
  ...rootElemProps
}) => {
  const { locale } = useGlobalConfigContext()
  const { saveUdpProductClickAttribution, trackUdpProductImpression } =
    useRecommendationWidgetContext()

  const items = useMemo(() => {
    if (!products) {
      /**
       * numbers will be returned to show skeleton items
       */
      return Array.from({ length: productsPerPage + 1 })
        .keys()
        .toArray()
    }

    // Filter out products that cannot be rendered as usable product cards.
    return products.filter((product) =>
      recommendationProductIsRenderable({
        locale,
        product: product.data,
      }),
    )
  }, [products, productsPerPage, locale])
  const ProductComponent = slots?.itemContent ?? RecommendationProductCard
  const { containerProps, leftRef, rightRef } =
    useSliderNavigation<HTMLUListElement>()

  return (
    <div
      className={clsx('recommendations-horizontal-slider', className)}
      {...rootElemProps}
    >
      <ul
        className={clsx(
          'recommendations-horizontal-slider__slider',
          !products && 'recommendations-horizontal-slider__slider_no-items',
        )}
        style={
          {
            '--items-per-page': String(productsPerPage),
          } as CSSProperties
        }
        {...containerProps}
      >
        {items.map((item, index) =>
          typeof item === 'number' ? (
            <li
              className="recommendations-horizontal-slider__item-wrapper"
              key={item}
            >
              <ProductComponent />
            </li>
          ) : (
            <li
              className="recommendations-horizontal-slider__item-wrapper"
              key={item.data.UID}
              ref={trackUdpProductImpression?.({
                position: index + 1,
                product: item,
              })}
            >
              <ProductComponent
                onProductLinkClick={(): void => {
                  saveUdpProductClickAttribution?.(item.data.UID)
                  onGtmDataLayerPush?.(
                    item.data.UID,
                    item.attributes.trackingtoken,
                  )
                  onProductLinkClick?.(item.data.UID)
                }}
                product={item.data}
                trackingToken={item.attributes.trackingtoken}
              />
            </li>
          ),
        )}
      </ul>
      {products !== undefined && products.length > productsPerPage && (
        <>
          <SliderButton
            aria-label="previous"
            className="recommendations-horizontal-slider__navigation-button left-0 hidden tablet:inline-flex"
            direction="left"
            onClick={onNavigationButtonClick}
            ref={leftRef}
          />
          <SliderButton
            aria-label="next"
            className="recommendations-horizontal-slider__navigation-button right-0 hidden tablet:inline-flex"
            direction="right"
            onClick={onNavigationButtonClick}
            ref={rightRef}
          />
        </>
      )}
    </div>
  )
}
